import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PulpPaperRoute } from '../../types';

/** 箱板纸机 P&ID 详细图：面/芯/底三层流浆箱 → 网部 → 压榨 → 干燥缸 → 压光 → 卷取 */
const media = {
  pulp: { label: '浆料', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  paper: { label: '纸页', light: '#6d4c41', dark: '#a1887f', width: 2.6 },
  steam: { label: '干燥蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  water: { label: '白水 / 冷凝水', light: '#1565c0', dark: '#64b5f6', width: 2 },
};

const devices = [
  { id: 'sb1', kind: 'hDrum' as const, x: 170, y: 96, tag: 'SB-101', name: '面层流浆箱' },
  { id: 'sb2', kind: 'hDrum' as const, x: 170, y: 189, tag: 'SB-102', name: '芯层流浆箱' },
  { id: 'sb3', kind: 'hDrum' as const, x: 170, y: 282, tag: 'SB-103', name: '底层流浆箱' },
  { id: 'wb', kind: 'washer' as const, x: 380, y: 188, tag: 'WB-101', name: '网部' },
  { id: 'pr', kind: 'washer' as const, x: 500, y: 188, tag: 'PR-101', name: '压榨部' },
  { id: 'dr1', kind: 'exchanger' as const, x: 640, y: 188, tag: 'DR-101', name: '前烘' },
  { id: 'sg', kind: 'washer' as const, x: 800, y: 181, tag: 'SG-101', name: '施胶机', note: '表面施胶' },
  { id: 'dr2', kind: 'exchanger' as const, x: 960, y: 188, tag: 'DR-102', name: '后烘' },
  { id: 'rd', kind: 'hDrum' as const, x: 1120, y: 189, tag: 'RD-101', name: '卷取' },
];

const ports: PidPort[] = [
  { id: 'p-face', label: '面层浆', medium: 'pulp', x: 0, y: 131, direction: 'in' as const },
  { id: 'p-core', label: '芯层浆', medium: 'pulp', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-bottom', label: '底层浆', medium: 'pulp', x: 0, y: 317, direction: 'in' as const },
  { id: 'p-stm', label: '干燥蒸汽', medium: 'steam', x: 600, y: 40, direction: 'in' as const },
  { id: 'p-paper', label: '成品纸卷', medium: 'paper', x: 1290, y: 224, direction: 'out' as const },
  { id: 'p-white', label: '白水回收', medium: 'water', x: 380, y: 611, direction: 'out' as const },
  { id: 'p-cond', label: '凝结水', medium: 'water', x: 940, y: 470, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  // 三层浆在网部合流成一张纸板
  { id: 'k01', from: 'p-face', fromSide: 'right', to: 'sb1', toSide: 'left', medium: 'pulp' },
  { id: 'k02', from: 'p-core', fromSide: 'right', to: 'sb2', toSide: 'left', medium: 'pulp' },
  { id: 'k03', from: 'p-bottom', fromSide: 'right', to: 'sb3', toSide: 'left', medium: 'pulp' },
  { id: 'k04', from: 'sb1', fromSide: 'right', to: 'wb', toSide: 'left', medium: 'pulp' },
  { id: 'k05', from: 'sb2', fromSide: 'right', to: 'wb', toSide: 'left', medium: 'pulp' },
  { id: 'k06', from: 'sb3', fromSide: 'right', to: 'wb', toSide: 'left', medium: 'pulp' },
  // 湿部 → 前烘 → 施胶 → 后烘 → 卷取（纸页）
  { id: 'k07', from: 'wb', fromSide: 'right', to: 'pr', toSide: 'left', medium: 'paper' },
  { id: 'k08', from: 'pr', fromSide: 'right', to: 'dr1', toSide: 'left', medium: 'paper' },
  { id: 'k09', from: 'dr1', fromSide: 'right', to: 'sg', toSide: 'left', medium: 'paper' },
  { id: 'k10', from: 'sg', fromSide: 'right', to: 'dr2', toSide: 'left', medium: 'paper' },
  { id: 'k12', from: 'dr2', fromSide: 'right', to: 'rd', toSide: 'left', medium: 'paper' },
  { id: 'k13', from: 'rd', fromSide: 'right', to: 'p-paper', toSide: 'left', medium: 'paper' },
  // 干燥蒸汽（上面车道）与凝结水（下面车道）
  { id: 'k14', from: 'p-stm', fromSide: 'left', to: 'dr1', toSide: 'top', medium: 'steam' },
  { id: 'k15', from: 'p-stm', fromSide: 'left', to: 'dr2', toSide: 'top', medium: 'steam' },
  { id: 'k17', from: 'dr1', fromSide: 'bottom', to: 'p-cond', toSide: 'left', medium: 'water' },
  { id: 'k18', from: 'dr2', fromSide: 'bottom', to: 'p-cond', toSide: 'left', medium: 'water' },
  // 网部白水
  { id: 'k20', from: 'wb', fromSide: 'bottom', to: 'p-white', toSide: 'left', medium: 'water' },
];

const LinerboardDiagram: React.FC<{ route: PulpPaperRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="箱板纸机详细流程"
    subtitle="面层/芯层/底层三个流浆箱在网部合流成型 → 压榨 → 前烘干燥 → 表面施胶 → 后烘干燥（蒸汽加热、凝结水回收）→ 卷取；网部白水回收（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={route.products}
  />
);

export default LinerboardDiagram;
